import { DateTime } from 'luxon';
import { getCurrentLocale } from './locale';

export const getYear = (isoDate: string): number => {
    return DateTime.fromISO(isoDate).year;
};

export const getMonth = (isoDate: string): number => {
    return DateTime.fromISO(isoDate).month;
};

export const getDay = (isoDate: string): number => {
    return DateTime.fromISO(isoDate).day;
};

// TODO: implement aliases to functions below

export const dateTimeISOFromSQL = (sqlDate: string): string => {
    return DateTime.fromSQL(sqlDate).toISO();
};

export const dateHumanReadableWithoutYear = (isoDate: string): string => {
    return DateTime.fromISO(isoDate).setLocale(getCurrentLocale()).toFormat('dd.M.');
};

export const dateHumanReadable = (isoDate: string): string => {
    return DateTime.fromISO(isoDate).setLocale(getCurrentLocale()).toLocaleString(DateTime.DATE_SHORT);
};

export const dateHumanReadableFromSQL = (isoDate: string): string => {
    return DateTime.fromSQL(isoDate).setLocale(getCurrentLocale()).toLocaleString(DateTime.DATE_SHORT);
};

export const timeHumanReadableFromISO = (isoDate: string): string => {
    return DateTime.fromISO(isoDate).setLocale(getCurrentLocale()).toLocaleString(DateTime.TIME_SIMPLE);
};

export const timeHumanReadableFromISOGetUtc = (isoDate: string): string => {
    return DateTime.fromISO(isoDate, { zone: 'UTC' })
        .setLocale(getCurrentLocale())
        .toLocaleString(DateTime.TIME_SIMPLE);
};

export const timeHumanReadable = (sqlDate: string): string => {
    return DateTime.fromSQL(sqlDate).setLocale(getCurrentLocale()).toLocaleString(DateTime.TIME_SIMPLE);
};

export const dateTimeHumanReadable = (sqlDate: string): string => {
    return DateTime.fromSQL(sqlDate).setLocale(getCurrentLocale()).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
};

export const dateTimeHumanReadableFromISO = (isoDate: string): string => {
    return DateTime.fromISO(isoDate).setLocale(getCurrentLocale()).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
};

export const dateTimeHumanReadableWithoutYearFromISO = (isoDate: string): string => {
    return DateTime.fromISO(isoDate).setLocale(getCurrentLocale()).toFormat('d.M. HH:mm');
};

export const dateTimeHumanReadableFromDatetimePicker = (isoDate: string): string => {
    return DateTime.fromISO(isoDate, { zone: 'UTC' })
        .setLocale(getCurrentLocale())
        .toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
};

export const dateTimeHumanReadableFromUnix = (unixDate: number): string => {
    return DateTime.fromMillis(unixDate)
        .setLocale(getCurrentLocale())
        .toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
};

export const dateTimeHumanReadableFromUnixSecs = (unixSeconds: number): string => {
    return DateTime.fromSeconds(unixSeconds)
        .setLocale(getCurrentLocale())
        .toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
};

export const relativeTimeFromUnixSecs = (unixDate: number): string => {
    return DateTime.fromSeconds(unixDate)
        .setLocale(getCurrentLocale())
        .toRelative({
            style: 'long',
            locale: getCurrentLocale(),
            base: DateTime.now(),
            padding: 60 * 1000,
        });
};

export const relativeTime = (isoDate: string): string => {
    return DateTime.fromISO(isoDate)
        .setLocale(getCurrentLocale())
        .toRelative({
            padding: 60 * 1000,
        });
};

export const monthYear = (month: number, year: number): string => {
    return DateTime.fromObject({ month, year }).setLocale(getCurrentLocale()).toFormat('LLLL yyyy');
};

export const diffInSeconds = (isoDate1: string, isoDate2: string): number => {
    const date1 = DateTime.fromISO(isoDate1);
    const date2 = DateTime.fromISO(isoDate2);

    const diff = date2.diff(date1, 'seconds');

    return Math.abs(diff.toObject().seconds);
};

/** -------------------------------------------------------------------
 * Refactored below
 * ------------------------------------------------------------------- */

export class DateHelper {
    private timezone: string;
    private localFormatDateShort: string = 'd. M. yyyy';
    private localFormatTimeShort: string = 'HH:mm';
    private dateTime: DateTime | null = null;

    constructor(
        date: string | DateTime | null = null,
        timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
    ) {
        this.timezone = timezone;
        if (date) {
            this.setDateTime(date);
        }
    }

    setDateTime(date: string | DateTime): void {
        if (date instanceof DateTime) {
            this.dateTime = date.setZone(this.timezone);
        } else {
            this.dateTime = this.parseDate(date).setZone(this.timezone);
        }
    }

    setZone(timezone: string): void {
        this.timezone = timezone;
        if (this.dateTime) {
            this.dateTime = this.dateTime.setZone(timezone);
        }
    }

    private parseDate(date: string): DateTime {
        let dateTime = DateTime.fromISO(date, { setZone: true });

        if (!dateTime.isValid) {
            dateTime = DateTime.fromSQL(date, { zone: this.timezone });

            if (!dateTime.isValid) {
                const sqlFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
                const sqlWithTimezoneFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d{3})? [+-]\d{2}:\d{2}$/;

                if (sqlFormat.test(date)) {
                    dateTime = DateTime.fromSQL(date, { zone: this.timezone });
                } else if (sqlWithTimezoneFormat.test(date)) {
                    dateTime = DateTime.fromISO(date, { setZone: true });
                } else {
                    throw new Error('Invalid date format');
                }
            }
        }

        return dateTime.setZone('UTC');
    }

    toISO(): string {
        return this.dateTime?.toISO() ?? '';
    }

    toISODate(): string {
        return this.dateTime?.toISODate() ?? '';
    }

    toISOTime(): string {
        return this.dateTime?.toISOTime() ?? '';
    }

    toLocal(): DateTime {
        return this.dateTime?.setZone(this.timezone) ?? DateTime.now().setZone(this.timezone);
    }

    toHumanDate(): string {
        return this.toLocal().toFormat(this.localFormatDateShort);
    }

    toHumanTime(): string {
        return this.toLocal().toFormat(this.localFormatTimeShort);
    }

    fromHumanDate(date: string): DateTime {
        return this.fromHumanDateShort(date).setZone(this.timezone);
    }

    fromHumanDateShort(date: string): DateTime {
        return DateTime.fromFormat(date, this.localFormatDateShort, { zone: this.timezone });
    }

    fromHumanTime(time: string, date: string = DateTime.now().toISODate()): DateTime {
        return this.fromHumanTimeShort(time, date).setZone(this.timezone);
    }

    /**
     * Parse time from string in format HH:mm
     *
     * @param time string in format HH:mm (24h)
     * @param date date in ISO format (yyyy-MM-dd) - default is today, date is used to parse correct timezone offset depending on daylight saving time
     * @returns DateTime object with time set
     */
    fromHumanTimeShort(time: string, date: string = DateTime.now().toISODate()): DateTime {
        const combinedDateTimeString = `${date} ${time}`;

        return DateTime.fromFormat(combinedDateTimeString, `yyyy-MM-dd ${this.localFormatTimeShort}`, {
            zone: this.timezone,
        });
    }
}
